import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { alpha, Box, ButtonBase, Collapse, Stack, Typography, useTheme } from '@mui/material';
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';

const SHIMMER_COOLDOWN_MS = 30_000;

function useShimmerOnView() {
  const ref = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver>(null);
  const [shimmer, setShimmer] = useState(false);
  const lastShimmerAt = useRef(0);

  const delayRef = useRef('0ms');

  const triggerShimmer = useCallback(() => {
    const now = Date.now();
    if (now - lastShimmerAt.current < SHIMMER_COOLDOWN_MS) {
      return;
    }
    lastShimmerAt.current = now;
    delayRef.current = `${Math.floor(Math.random() * 1000)}ms`;
    setShimmer(true);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const mountDelay = setTimeout(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            triggerShimmer();
          }
        },
        { threshold: 0.3 },
      );
      observer.observe(el);
      observerRef.current = observer;
    }, 800);

    return () => {
      clearTimeout(mountDelay);
      observerRef.current?.disconnect();
    };
  }, [triggerShimmer]);

  return { ref, shimmer, delay: delayRef, onAnimationEnd: () => setShimmer(false) };
}

export interface IntroCardStep {
  icon: ReactNode;
  title: string;
  description: string;
}

interface IntroTriggerProps {
  open: boolean;
  onToggle: () => void;
  label: string;
  ariaLabel: string;
}

/**
 * Compact "how it works" trigger for desktop command bars: same control
 * vocabulary as toolbar filter buttons, info accent. Pair with an
 * <IntroPanel> inside a <Collapse in={open}> below the bar.
 */
export function IntroTrigger({ open, onToggle, label, ariaLabel }: IntroTriggerProps) {
  const theme = useTheme();
  const accent = theme.palette.info.main;

  return (
    <ButtonBase
      onClick={onToggle}
      aria-expanded={open}
      aria-label={ariaLabel}
      sx={{
        flexShrink: 0,
        gap: 0.75,
        px: 1.75,
        minHeight: 40,
        borderRadius: 1,
        border: '1px solid',
        borderColor: open ? accent : 'divider',
        color: open ? accent : 'text.secondary',
        backgroundColor: open ? alpha(accent, 0.06) : 'transparent',
        transition: 'background-color 0.2s, border-color 0.2s, color 0.2s',
        '&:hover': { backgroundColor: alpha(accent, 0.08) },
      }}
    >
      <HelpOutlineOutlinedIcon sx={{ fontSize: 20 }} />
      <Typography
        sx={{ fontWeight: 600, fontSize: '0.85rem', lineHeight: 1, whiteSpace: 'nowrap' }}
      >
        {label}
      </Typography>
    </ButtonBase>
  );
}

interface IntroPanelProps {
  headline: string;
  steps: IntroCardStep[];
}

/** Info-tinted panel with the 3-step explainer, opened by <IntroTrigger>. */
export function IntroPanel({ headline, steps }: IntroPanelProps) {
  const theme = useTheme();
  const accent = theme.palette.info.main;

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: alpha(accent, 0.18),
        background: `linear-gradient(135deg, ${alpha(accent, 0.08)} 0%, ${alpha(accent, 0.02)} 100%)`,
        px: 2,
        py: 2,
      }}
    >
      <IntroCardSteps headline={headline} steps={steps} />
    </Box>
  );
}

interface IntroCardStepsProps {
  headline: string;
  steps: IntroCardStep[];
  accentColor?: string;
}

export function IntroCardSteps({ headline, steps, accentColor }: IntroCardStepsProps) {
  const theme = useTheme();
  const accent = accentColor ?? theme.palette.info.main;

  return (
    <>
      <Typography
        variant="body2"
        sx={{
          color: 'text.primary',
          fontWeight: 600,
          lineHeight: 1.45,
          mb: 1.75,
          fontSize: '0.9rem',
        }}
      >
        {headline}
      </Typography>

      <Stack spacing={1.25}>
        {steps.map((step, idx) => (
          <Box
            key={step.title}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                width: 32,
                height: 32,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(accent, 0.12),
                color: accent,
                position: 'relative',
                '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
              }}
              aria-hidden="true"
            >
              {step.icon}
              <Box
                component="span"
                sx={{
                  position: 'absolute',
                  bottom: -4,
                  right: -4,
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  bgcolor: 'background.paper',
                  color: accent,
                  border: `1px solid ${alpha(accent, 0.3)}`,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                }}
              >
                {idx + 1}
              </Box>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.3, mb: 0.25 }}
              >
                {step.title}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  lineHeight: 1.45,
                  fontSize: '0.78rem',
                }}
              >
                {step.description}
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>
    </>
  );
}

interface IntroCardProps {
  /** Tracked-uppercase kicker — omit it: the headline carries its own weight. */
  eyebrow?: string;
  collapsedHint: string;
  headline: string;
  ariaLabel: string;
  steps: IntroCardStep[];
  defaultExpanded?: boolean;
  /** Closing note under the steps, for a caveat the steps must not bury. Accepts
   * `ReactNode` so a legal reference inside it can be rendered clickable
   * (e.g. via `<LegalRefInlineText>`) instead of plain, non-actionable text. */
  footnote?: ReactNode;
  /** Overrides the info accent on surfaces with their own palette (public pages). */
  accentColor?: string;
}

export function IntroCard({
  eyebrow,
  collapsedHint,
  headline,
  ariaLabel,
  steps,
  defaultExpanded = false,
  footnote,
  accentColor,
}: IntroCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const contentId = useId();
  const theme = useTheme();
  const accent = accentColor ?? theme.palette.info.main;
  const { ref: shimmerRef, shimmer, delay: delayRef, onAnimationEnd } = useShimmerOnView();

  return (
    <Box
      ref={shimmerRef}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 3,
        border: '1px solid',
        borderColor: alpha(accent, 0.18),
        background: `linear-gradient(135deg, ${alpha(accent, 0.08)} 0%, ${alpha(accent, 0.02)} 100%)`,
        '@keyframes shimmer': {
          '0%': { transform: 'translateX(-100%)' },
          '100%': { transform: 'translateX(100%)' },
        },
      }}
    >
      {shimmer && (
        <Box
          onAnimationEnd={onAnimationEnd}
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            pointerEvents: 'none',
            transform: 'translateX(-100%)',
            background: `linear-gradient(90deg, transparent 0%, ${alpha(accent, 0.12)} 50%, transparent 100%)`,
            animation: `shimmer 4s ease-in-out ${delayRef.current} forwards`,
          }}
        />
      )}
      <ButtonBase
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls={contentId}
        aria-label={ariaLabel}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          px: { xs: 1.75, sm: 2 },
          py: 1.5,
          textAlign: 'left',
          borderRadius: 3,
          '&:hover': {
            bgcolor: alpha(accent, 0.04),
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 2,
              bgcolor: alpha(accent, 0.12),
              color: accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              '& .MuiSvgIcon-root': { fontSize: '1.15rem' },
            }}
            aria-hidden="true"
          >
            <HelpOutlineOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            {eyebrow && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  fontWeight: 700,
                  color: accent,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontSize: '0.68rem',
                  lineHeight: 1.2,
                }}
              >
                {eyebrow}
              </Typography>
            )}
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: '0.88rem',
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {collapsedHint}
            </Typography>
          </Box>
        </Box>
        <ExpandMoreIcon
          sx={{
            color: accent,
            transition: 'transform 0.2s ease',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            flexShrink: 0,
          }}
        />
      </ButtonBase>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box id={contentId} sx={{ px: { xs: 1.75, sm: 2 }, pb: 2, pt: 0.5 }}>
          <IntroCardSteps headline={headline} steps={steps} accentColor={accent} />
          {footnote && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 1.75,
                px: 1.25,
                py: 1,
                borderRadius: 2,
                bgcolor: alpha(accent, 0.07),
                color: 'text.secondary',
                lineHeight: 1.5,
                fontSize: '0.78rem',
              }}
            >
              {footnote}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
